import React, { useEffect, useState } from "react"
import {
  Box, Button as ChakraButton, Center, Flex, Heading, Tag, Text,
} from "@chakra-ui/react"
import { useHistory, useParams } from "react-router-dom"
import { useQueryClient } from "react-query"
import Icon from "components/elements/Icon"
import GuestAvatar from "components/elements/GuestAvatar"
import { Combobox } from "components/Combobox"
import Link from "components/elements/Link"
import UpgradeAlert from "components/elements/UpgradeAlert"
import Modal from "components/dialogs/Modal"
import Button from "components/Buttons/Button"
import CancelButton from "components/Buttons/CancelButton"
import useModal from "services/useModal"
import { linkGuest, unlinkGuest } from "api/guests"
import { refetchGuests } from "context/actions"
import { useEventGuestPaths } from "utilities/routes"
import { PLUS_ONE_ARTICLE_LINK } from "utilities/externalLinks"
import { sortPlusOnes } from "utilities/arrays"
import { Guest, GuestSummary, ModalName } from "sharedTypes"
import DropdownToggle from "../DropdownToggle"

// 1 main guest + up to 9 plus-ones = 10-person max group (BE-enforced).
const PLUS_ONE_LIMIT = 9

type Props = {
  guest: Guest
  enabled: boolean
  disabled: boolean
}

const Group = ({
  guest, guest: { group }, enabled, disabled,
}: Props) => {
  const { eventId } = useParams<{ eventId: string }>()
  const queryClient = useQueryClient()
  const history = useHistory()
  const showModal = useModal()
  const { eventGuestPath } = useEventGuestPaths()

  // Unlink-confirm state is held HERE, not inside the popover, so the
  // confirmation modal survives the popover closing. Otherwise: clicking
  // UNLINK opens the modal, focus moves to the modal, closeOnBlur closes
  // the popover, the popover content unmounts, the modal (rendered as a
  // child of the popover) unmounts with it. Lifting the state out keeps
  // the modal alive across the popover close.
  const [unlinkTarget, setUnlinkTarget] = useState<GuestSummary | null>(null)

  const [mainGuest, ...plusOnes] = group

  // If a refetch / external mutation removes the targeted plus-one from the
  // group while the modal is open, close the modal — confirming would POST a
  // now-invalid id.
  useEffect(() => {
    if (!unlinkTarget) return
    const stillInGroup = group.some(({ id }) => id === unlinkTarget.id)
    if (!stillInGroup) setUnlinkTarget(null)
  }, [group, unlinkTarget])
  // Empty when the guest has no plus-ones — Combobox.Empty renders the
  // educational state in that case (matches the legacy behavior of hiding
  // the list entirely when there's only the main guest).
  const members: GuestSummary[] = plusOnes.length > 0
    ? [mainGuest, ...sortPlusOnes(plusOnes)]
    : []
  const currentMember = members.find(({ id }) => id === guest.id) || null

  const triggerTitle = group.length > 1 ? `Group of ${group.length}` : "Group"
  const atLimit = plusOnes.length >= PLUS_ONE_LIMIT

  const onChange = (member: GuestSummary | null) => {
    if (!member) return
    history.push(eventGuestPath(eventId, member.id))
  }

  const confirmUnlink = () => {
    if (!unlinkTarget) return
    unlinkGuest(eventId, unlinkTarget.id).then(() => {
      refetchGuests(queryClient, eventId)
    })
    setUnlinkTarget(null)
  }

  const openLinkModal = () => showModal(ModalName.SelectGuest, {
    onSelect: (selectedGuest: GuestSummary) => {
      linkGuest(eventId, mainGuest.id, selectedGuest.id).then(() => {
        refetchGuests(queryClient, eventId)
      })
    },
    showMenu: false,
    showTables: false,
    disableMainGuests: true,
    disabledGuestIds: [mainGuest.id, ...plusOnes.map(({ id }) => id)],
  })

  const openNewPlusOnesModal = () => showModal(ModalName.NewPlusOnes, {
    guestId: mainGuest.id,
    plusOnesCount: plusOnes.length,
    guestName: mainGuest.personInfo.firstName,
  })

  const unlinkName = unlinkTarget
    ? `${unlinkTarget.personInfo.firstName} ${unlinkTarget.personInfo.lastName}`
    : ""

  return (
    <>
      <Combobox.Root<GuestSummary>
        items={members}
        getItemKey={(m) => String(m.id)}
        getItemLabel={(m) => `${m.personInfo.firstName} ${m.personInfo.lastName}`}
        value={currentMember}
        onChange={onChange}
      >
        <Combobox.Trigger>
          <Box
            pointerEvents={disabled ? "none" : "auto"}
            opacity={disabled ? 0.6 : 1}
          >
            <DropdownToggle
              icon={<Icon icon="guest-group" size={6} mr={2} />}
              title={triggerTitle}
              disabled={disabled}
            />
          </Box>
        </Combobox.Trigger>
        <Combobox.Content w="360px">
          <Combobox.Header>Group</Combobox.Header>
          <Combobox.List<GuestSummary> maxH="400px">
            {(member, index) => {
              const isMain = index === 0

              return (
                // divideAfter on every row gives the legacy between-row
                // borders; the last visible row's border is suppressed
                // automatically.
                <Combobox.Item item={member} divideAfter>
                  <Flex align="center" gap={3} w="full">
                    {/* flexShrink={0} keeps the avatar circular — without
                        it, flex squashes it into an oval when names are
                        long. */}
                    <Box flexShrink={0}>
                      <GuestAvatar
                        {...member.personInfo}
                        status={member.status}
                        checkedIn={!!member.checkedIn}
                      />
                    </Box>
                    <Text mr="auto" noOfLines={1} fontWeight="semibold">
                      {member.personInfo.firstName} {member.personInfo.lastName}
                    </Text>
                    {isMain ? (
                      <Tag
                        variant="outline"
                        w={16}
                        fontSize="xs"
                        fontWeight="bold"
                        justifyContent="center"
                        boxShadow="none"
                        borderColor="gray.800"
                        borderWidth={1}
                        color="gray.800"
                      >
                        MAIN
                      </Tag>
                    ) : (
                      <ChakraButton
                        w={16}
                        size="xs"
                        color="orange.400"
                        borderColor="orange.400"
                        variant="outline"
                        onClick={(e) => {
                          // Click stays on the UNLINK button — don't bubble
                          // into the row's selectItem, which would navigate
                          // away before the modal opens.
                          e.stopPropagation()
                          setUnlinkTarget(member)
                        }}
                      >
                        UNLINK
                      </ChakraButton>
                    )}
                  </Flex>
                </Combobox.Item>
              )
            }}
          </Combobox.List>
          <Combobox.Empty>
            {() => (
              <Flex direction="column" align="center" p={6}>
                <Icon icon="guest-group" size={10} />
                <Text align="center">
                  Groups allow you to link guests and their plus-ones to help
                  you manage them together.
                  <br />
                  <Link target="_blank" href={PLUS_ONE_ARTICLE_LINK}>Learn More</Link>
                </Text>
              </Flex>
            )}
          </Combobox.Empty>
          {!enabled && (
            <Box p={4} borderTopWidth={1} borderColor="gray.200">
              <UpgradeAlert text="This feature requires a Professional Subscription." />
            </Box>
          )}
          {enabled && atLimit && (
            <Box
              textAlign="center"
              p={4}
              color="gray.600"
              borderTopWidth={1}
              borderColor="gray.200"
            >
              <Heading size="sm" fontWeight="semibold" mb={2}>
                Plus-One Limit Reached
              </Heading>
              <Text>
                Each guest group can have a max size of{" "}
                <Text as="strong">10</Text> total people.
              </Text>
            </Box>
          )}
          {enabled && !atLimit && (
            <>
              <FooterAction
                icon="external-link"
                label="Link Existing Guest"
                onSelect={openLinkModal}
              />
              <FooterAction
                icon="plus"
                label="Create New Plus-Ones"
                onSelect={openNewPlusOnesModal}
              />
            </>
          )}
        </Combobox.Content>
      </Combobox.Root>

      <Modal
        size="lg"
        show={!!unlinkTarget}
        hide={() => setUnlinkTarget(null)}
        title="Unlink Guest"
        actions={[
          <CancelButton key="cancel" onClick={() => setUnlinkTarget(null)} />,
          <Button
            key="unlink"
            onClick={confirmUnlink}
            colorScheme="button.danger"
          >
            Unlink
          </Button>,
        ]}
      >
        <Text size="xl">
          Are you sure you want to unlink{" "}
          <Text as="strong">{unlinkName}</Text>{" "}
          from this group?
        </Text>
        <Text mt={4}>
          This will cause{" "}
          <Text as="strong">{unlinkName}</Text>{" "}
          to become independent and not attached to any other guests.
        </Text>
      </Modal>
    </>
  )
}

type FooterActionProps = {
  icon: string
  label: string
  onSelect: () => void
}

// pl={10} pushes the circle icon out to the avatar column of the list rows
// (matches the legacy MenuItem indent). py={2} matches the list-row density
// so the footer reads as a continuation, not a heavier action band. The
// circle's w/h={10} matches the GuestAvatar size in the rows above.
const FooterAction = ({ icon, label, onSelect }: FooterActionProps) => (
  <Combobox.Action
    onSelect={onSelect}
    pl={10}
    py={2}
    borderTopWidth={1}
    borderColor="gray.200"
  >
    <Center
      display="inline-flex"
      w={10}
      h={10}
      bg="blue.400"
      borderRadius="full"
      mr={3}
      flexShrink={0}
    >
      <Icon icon={icon} size={4} color="white" />
    </Center>
    <Text fontWeight="semibold">{label}</Text>
  </Combobox.Action>
)

export default Group
